import Modal from "../Modal";
import useUpdateProfileDetails from "../../hooks/profile/useUpdateProfileDetailsModal";
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from "react-i18next";

const UpdateProfileDetailsModal = ({ username }) => {
  const useUpdateProfileModal = useUpdateProfileDetails();
  const {t} = useTranslation('profile')
  const translation = t('profile-edit')

  const [profileErrorMessage, setProfileErrorMessage] = useState('');
  const [postUpdateSuccessMessage, setPostUpdateSuccessMessage] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullname: '',
    // username: '',
    religion: '',
    dob: '',
    introduction: '',
    address: '',
    phone_number: '',
  });

  const [originalData, setOriginalData] = useState({
    fullname: '',
    // username: '',
    religion: '',
    dob: '',
    introduction: '',
    address: '',
    phone_number: '',
  });


  useEffect(() => {
    const fetchProfileData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_DOMAIN}/user/update-user-profile-details/`,
          {
            params: { username },
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            },
          }
        );
        if (response.status === 200) {
          setFormData(response.data);
          setOriginalData(response.data); // Store the original data
        }
      } catch (error) {
        // setErrorMessage('Failed to fetch profile data');
      } finally {
        setIsLoading(false);
      }
    };
  
    if (useUpdateProfileModal.isOpen) {
      fetchProfileData();
    }
  }, [useUpdateProfileModal.isOpen, username]);

  const handlePostDataChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData((prevData) => ({
      ...prevData,
      [name]: val,
    }));
  };

  const SubmitPostData = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    setProfileErrorMessage('');

    // Client-side validation
    const requiredFields = {
      fullname: translation.fullname,
    };

    const missingField = Object.entries(requiredFields).find(
      ([field]) => !formData[field]?.toString().trim()
    );

    if (missingField) {
      const label = missingField[1];
      setProfileErrorMessage(`${label}: ${translation.required_field}`);
      setIsLoading(false);
      return;
    }

    // Compare originalData and formData, only keep changed fields
    const updatedFields = Object.keys(formData).reduce((acc, key) => {
      if (formData[key] !== originalData[key]) {
        acc[key] = formData[key];
      }
      return acc;
    }, {});


    if (Object.keys(updatedFields).length === 0) {
      setProfileErrorMessage(`${translation.no_changes_detected}`); 
      setIsLoading(false);
      return;
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    };

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_DOMAIN}/user/update-user-profile-details/`,
        JSON.stringify(updatedFields),
        config
      );
    
      if (response.status === 200) {
        setIsLoading(false);
        setProfileErrorMessage('');
        setPostUpdateSuccessMessage(`${translation.submit_success}`);
        
        // Use setTimeout to delay modal closing and reset messages
        setTimeout(() => {
          useUpdateProfileModal.close();
          setPostUpdateSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      setIsLoading(false);
    
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        
        if (errorData?.error && typeof errorData?.error === "object") {
          if (Array.isArray(errorData.error?.error)) {
            setProfileErrorMessage(errorData.error.error[0]);
          } else if (errorData.error.fullname?.length) {
            setProfileErrorMessage(`${translation.fullname}` + errorData.error.fullname[0]);
          } else if (errorData.error.address?.length) {
            setProfileErrorMessage(`${translation.address}`  + errorData.error.address[0]);
          } else if (errorData.error.phone_number?.length) {
            setProfileErrorMessage(`${translation.phone_number}`+ errorData.error.phone_number[0]);
          } else if (errorData.error.dob?.length) {
            setProfileErrorMessage(`${translation.dob}` + errorData.error.dob[0]);
          } else if (errorData.error.religion?.length) {
            setProfileErrorMessage(`${translation.religion}`  + errorData.error.religion[0]);
          } else if (errorData.error.introduction?.length) {
            setProfileErrorMessage(`${translation.introduction}`+ errorData.error.introduction[0]);
          } else {
            setProfileErrorMessage(`${translation.unexpected_error}`);
          }
        } else if (typeof errorData?.error === "string") {
          setProfileErrorMessage(errorData.error);
        } else {
          setProfileErrorMessage(`${translation.unexpected_error}`);
        }
      } else {
        setProfileErrorMessage(`${translation.unexpected_error}`);
      }
    }
  };

  const content = (
    <>
      <div className="pt-2 pb-5 space-y-5">
        <div className="flex flex-col">
          <label htmlFor="fullname" className="block font-semibold mb-2 dark:text-white">{translation.fullname}<span className="font-bold size-5 text-red">*</span></label>
          <input
            type="text"
            id="fullname"
            name="fullname"
            placeholder={translation.enter_fullname}
            value={formData.fullname}
            onChange={handlePostDataChange}
            className="az-input-btn"
            required
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="address" className="block font-semibold mb-2 dark:text-white">{translation.address}</label>
          <input
            type="text"
            id="address"
            name="address"
            placeholder={translation.enter_address}
            value={formData.address}
            onChange={handlePostDataChange}
            className="az-input-btn"
            required
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="phone_number" className="block font-semibold mb-2 dark:text-white">{translation.phone_number}</label>
          <input
            type="tel"
            id="phone_number"
            name="phone_number"
            placeholder={translation.enter_phonenumber} 
            value={formData.phone_number}
            onKeyDown={(e) => {
              if (!/[0-9]/.test(e.key) && 
                  !["Backspace", "Delete", "Tab", "Enter", "ArrowLeft", "ArrowRight"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            min="0"
            step="1"
            inputMode="numeric"
            onChange={handlePostDataChange}
            className="az-input-btn"
            required
            maxLength={11}
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="dob" className="block font-semibold mb-2 dark:text-white">{translation.dob}</label>
          <input
            type="date"
            id="dob"
            name="dob"
            value={formData.dob}
            onChange={handlePostDataChange}
            className="az-input-btn dark:bg-gray-800 dark:text-white"
            required
            min={new Date(new Date().setFullYear(new Date().getFullYear() - 90)).toISOString().split("T")[0]}
            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]}
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="religion" className="block font-semibold mb-2 dark:text-white">{translation.religion}</label>
          <select
            id="religion"
            name="religion"
            value={formData.religion}
            onChange={handlePostDataChange}
            className="az-input-btn"
            required
          >
            <option value="">{translation.religion_details.select}</option>
            <option value="Islam">{translation.religion_details.islam}</option>
            <option value="Hindu">{translation.religion_details.hindu}</option>
            <option value="Christian">{translation.religion_details.christian}</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label htmlFor="introduction" className="block font-semibold mb-2 dark:text-white">{translation.introduction}</label>
          <textarea
            id="introduction"
            name="introduction"
            value={formData.introduction}
            onChange={handlePostDataChange}
            className="az-input-btn"
            required
          />
        </div>
      </div>

      <div className="text-center">
        {isLoading ? (
          <div className="inline-block loading-icon">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-stone-100" />
          </div>
        ) : (
          <button
            onClick={SubmitPostData}
            className="w-full rounded-md bg-blue-500 text-white font-extrabold px-4 py-2 hover:bg-blue-600"
          >
            {translation.submit}
          </button>
        )}

        {profileErrorMessage && <div className="flex justify-center dark:text-green-100 font-semibold text-red m-2 ">{profileErrorMessage}</div>}

        
        {postUpdateSuccessMessage && <div style={{ color: 'green', fontWeight: 'bold' }}>{postUpdateSuccessMessage}</div>}
      </div>
    </>
  );

  return (
    <Modal
      isOpen={useUpdateProfileModal.isOpen}
      close={useUpdateProfileModal.close}
      label={translation.update_profile_modal}
      content={content}
    />
  );
};

export default UpdateProfileDetailsModal;